'use client'

import { useState } from 'react'
import Link from 'next/link'
import {
  AlertTriangle,
  AlertCircle,
  Info,
  ArrowRight,
  Sparkles,
  CircleCheck,
  ChevronDown,
  ChevronUp,
  Copy,
  Check,
  CircleX,
  Mail,
  Loader2,
} from 'lucide-react'
import {
  LenisFadeIn,
  LenisStaggered,
} from '@/components/animations/LenisReveal'
import OverallScore from './OverallScore'
import BotRulesTable from './BotRulesTable'
import DirectiveBreakdown from './DirectiveBreakdown'
import PlatformCTA from '@/components/platform/PlatformCTA'

// --- Types (match API response) ---

interface BotAnalysis {
  bot: string
  category: 'search' | 'ai-browse' | 'ai-train' | 'seo'
  hasExplicitRule: boolean
  matchedUserAgent: string | null
  status: 'allowed' | 'blocked' | 'partial' | 'no-rule'
  allowedPaths: string[]
  disallowedPaths: string[]
  crawlDelay: number | null
  description: string
}

interface ScoreBreakdown {
  category: string
  score: number
  maxScore: number
  details: string
  color: string
}

interface UserAgentBlock {
  userAgent: string
  allowPaths: string[]
  disallowPaths: string[]
  crawlDelay: number | null
  lineRange: [number, number]
}

interface SyntaxIssue {
  line: number
  message: string
  severity: 'error' | 'warning'
}

export interface RobotsAnalyzerResult {
  url: string
  domain: string
  checkedAt: string
  overallScore: number
  grade: 'A' | 'B' | 'C' | 'D' | 'F'
  robotsTxt: {
    exists: boolean
    blocked: boolean
    rawContent: string
    fileSize: number
    lineCount: number
    sitemapUrls: string[]
  }
  userAgentBlocks: UserAgentBlock[]
  botAnalysis: BotAnalysis[]
  syntaxIssues: SyntaxIssue[]
  scoreBreakdown: ScoreBreakdown[]
  recommendations: {
    priority: 'high' | 'medium' | 'low'
    category: string
    message: string
  }[]
  suggestedSnippet: string | null
  aiSummary: {
    summary: string
    actions: string[]
    context: string
  } | null
}

interface AnalyzerResultsProps {
  result: RobotsAnalyzerResult
  /**
   * 'public' (default) renders the full marketing surface. 'dashboard' hides
   * public-only chrome (the email-share row, `PlatformCTA`, and the gradient
   * services CTA) so the SAME rich report can be reused inside `/platform/app`.
   * Result content (score ring, status badges, AI analysis, bot-rules table,
   * score breakdown, directive breakdown, syntax issues, suggested snippet,
   * recommendations, raw robots.txt) is identical either way.
   */
  variant?: 'public' | 'dashboard'
}

const PRIORITY_ICONS: Record<string, React.ReactNode> = {
  high: <AlertTriangle size={14} style={{ color: '#F90B8A' }} />,
  medium: <AlertCircle size={14} style={{ color: '#FDC304' }} />,
  low: <Info size={14} style={{ color: '#008dcc' }} />,
}

const PRIORITY_COLORS: Record<string, { bg: string; text: string }> = {
  high: { bg: '#F90B8A15', text: '#F90B8A' },
  medium: { bg: '#FDC30415', text: '#FDC304' },
  low: { bg: '#008dcc15', text: '#008dcc' },
}

export default function AnalyzerResults({
  result,
  variant = 'public',
}: AnalyzerResultsProps) {
  const [rawExpanded, setRawExpanded] = useState(false)
  const [snippetCopied, setSnippetCopied] = useState(false)
  const [showEmailInput, setShowEmailInput] = useState(false)
  const [shareEmail, setShareEmail] = useState('')
  const [shareStatus, setShareStatus] = useState<
    'idle' | 'sending' | 'sent' | 'error'
  >('idle')
  const [shareError, setShareError] = useState('')

  async function handleEmailShare() {
    if (
      !shareEmail.trim() ||
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(shareEmail.trim())
    ) {
      setShareError('Please enter a valid email address.')
      setShareStatus('error')
      return
    }

    setShareStatus('sending')
    setShareError('')

    try {
      const res = await fetch('/api/tools/robots-analyzer/share', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: shareEmail.trim(),
          url: result.url,
          domain: result.domain,
          overallScore: result.overallScore,
          grade: result.grade,
          robotsTxtExists: result.robotsTxt.exists,
          totalBots: result.botAnalysis.length,
          botsWithRules: result.botAnalysis.filter(b => b.hasExplicitRule)
            .length,
          syntaxIssues: result.syntaxIssues.length,
          scoreBreakdown: result.scoreBreakdown,
          recommendations: result.recommendations,
          aiSummary: result.aiSummary?.summary || null,
        }),
      })

      if (!res.ok) {
        const data = await res.json().catch(() => null)
        throw new Error(data?.message || 'Failed to send email.')
      }

      setShareStatus('sent')
      setTimeout(() => {
        setShareStatus('idle')
        setShowEmailInput(false)
        setShareEmail('')
      }, 2000)
    } catch (err) {
      setShareError(
        err instanceof Error ? err.message : 'Failed to send email.'
      )
      setShareStatus('error')
    }
  }

  const botsWithRules = result.botAnalysis.filter(b => b.hasExplicitRule).length
  const syntaxErrors = result.syntaxIssues.filter(
    i => i.severity === 'error'
  ).length
  const syntaxWarnings = result.syntaxIssues.filter(
    i => i.severity === 'warning'
  ).length

  function copySnippet() {
    if (result.suggestedSnippet) {
      navigator.clipboard.writeText(result.suggestedSnippet)
      setSnippetCopied(true)
      setTimeout(() => setSnippetCopied(false), 2000)
    }
  }

  return (
    <div className='space-y-8'>
      {/* Overall Score */}
      <LenisFadeIn>
        <div className='bg-muted/50 rounded-2xl border border-border/60 p-6'>
          <div className='text-center mb-2'>
            <p className='text-xs text-muted-foreground'>
              robots.txt Analysis for
            </p>
            <p className='text-sm font-medium text-foreground truncate max-w-lg mx-auto'>
              {result.domain}
            </p>
          </div>
          <OverallScore score={result.overallScore} grade={result.grade} />

          {/* Quick status badges */}
          <div className='flex flex-wrap justify-center gap-2 mt-2'>
            <span
              className='inline-flex items-center gap-1.5 text-[10px] font-medium rounded-full px-2.5 py-1'
              style={{
                backgroundColor: result.robotsTxt.exists
                  ? '#22c55e15'
                  : '#F90B8A15',
                color: result.robotsTxt.exists ? '#22c55e' : '#F90B8A',
              }}
            >
              {result.robotsTxt.exists ? (
                <CircleCheck size={12} />
              ) : (
                <AlertTriangle size={12} />
              )}
              {result.robotsTxt.exists
                ? 'robots.txt found'
                : 'robots.txt missing'}
            </span>

            <span
              className='inline-flex items-center gap-1.5 text-[10px] font-medium rounded-full px-2.5 py-1'
              style={{
                backgroundColor:
                  result.robotsTxt.sitemapUrls.length > 0
                    ? '#22c55e15'
                    : '#FDC30415',
                color:
                  result.robotsTxt.sitemapUrls.length > 0
                    ? '#22c55e'
                    : '#FDC304',
              }}
            >
              {result.robotsTxt.sitemapUrls.length > 0 ? (
                <CircleCheck size={12} />
              ) : (
                <AlertCircle size={12} />
              )}
              {result.robotsTxt.sitemapUrls.length > 0
                ? `${result.robotsTxt.sitemapUrls.length} sitemap(s)`
                : 'No sitemap'}
            </span>

            <span
              className='inline-flex items-center gap-1.5 text-[10px] font-medium rounded-full px-2.5 py-1'
              style={{
                backgroundColor: botsWithRules >= 5 ? '#22c55e15' : '#FDC30415',
                color: botsWithRules >= 5 ? '#22c55e' : '#FDC304',
              }}
            >
              {botsWithRules >= 5 ? (
                <CircleCheck size={12} />
              ) : (
                <AlertCircle size={12} />
              )}
              {botsWithRules} bot rules
            </span>

            {result.syntaxIssues.length > 0 && (
              <span
                className='inline-flex items-center gap-1.5 text-[10px] font-medium rounded-full px-2.5 py-1'
                style={{
                  backgroundColor: syntaxErrors > 0 ? '#F90B8A15' : '#FDC30415',
                  color: syntaxErrors > 0 ? '#F90B8A' : '#FDC304',
                }}
              >
                <AlertTriangle size={12} />
                {syntaxErrors > 0
                  ? `${syntaxErrors} error(s)`
                  : `${syntaxWarnings} warning(s)`}
              </span>
            )}
          </div>
        </div>
      </LenisFadeIn>

      {/* Email share — public surface only */}
      {variant !== 'dashboard' && (
        <div className='flex flex-col items-center gap-2'>
          {!showEmailInput ? (
            <button
              onClick={() => setShowEmailInput(true)}
              className='inline-flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-full border border-border/60 bg-background text-foreground/80 hover:text-foreground transition-colors'
            >
              <Mail size={16} />
              Email results
            </button>
          ) : (
            <div className='flex items-center gap-2 w-full max-w-md'>
              <input
                type='email'
                value={shareEmail}
                onChange={e => {
                  setShareEmail(e.target.value)
                  if (shareStatus === 'error') setShareStatus('idle')
                }}
                placeholder='colleague@company.com'
                className='flex-1 h-10 px-4 text-sm rounded-full border border-border/60 bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40'
                onKeyDown={e => {
                  if (e.key === 'Enter') handleEmailShare()
                }}
                disabled={shareStatus === 'sending' || shareStatus === 'sent'}
                autoFocus
              />
              <button
                onClick={handleEmailShare}
                disabled={shareStatus === 'sending' || shareStatus === 'sent'}
                className='inline-flex items-center gap-2 text-sm font-medium h-10 px-5 rounded-full border border-border/60 bg-background text-foreground/80 hover:text-foreground transition-colors disabled:opacity-50'
              >
                {shareStatus === 'sending' ? (
                  <Loader2 size={16} className='animate-spin' />
                ) : shareStatus === 'sent' ? (
                  <>
                    <Check size={16} style={{ color: '#22c55e' }} />
                    <span style={{ color: '#22c55e' }}>Sent!</span>
                  </>
                ) : (
                  'Send'
                )}
              </button>
            </div>
          )}
          {shareStatus === 'error' && shareError && (
            <p className='text-xs' style={{ color: '#F90B8A' }}>
              {shareError}
            </p>
          )}
        </div>
      )}

      {variant !== 'dashboard' && (
        <PlatformCTA
          currentTool='Robots.txt'
          score={result.overallScore}
          grade={result.grade}
        />
      )}

      {/* AI Summary */}
      {result.aiSummary && (
        <LenisFadeIn>
          <div className='bg-muted/50 rounded-2xl border border-border/60 p-6'>
            <h2 className='text-lg font-semibold text-foreground mb-4 flex items-center gap-2'>
              <Sparkles size={20} style={{ color: '#8b5cf6' }} />
              AI Analysis
            </h2>

            <p className='text-sm text-foreground/90 leading-relaxed mb-4'>
              {result.aiSummary.summary}
            </p>

            {result.aiSummary.actions.length > 0 && (
              <div className='space-y-2 mb-4'>
                <h3 className='text-xs font-semibold text-muted-foreground uppercase tracking-wide'>
                  Priority Actions
                </h3>
                <ul className='space-y-2'>
                  {result.aiSummary.actions.map((action, i) => (
                    <li
                      key={i}
                      className='flex items-start gap-2.5 text-sm text-foreground/80'
                    >
                      <CircleCheck
                        size={16}
                        className='mt-0.5 shrink-0'
                        style={{ color: '#008dcc' }}
                      />
                      {action}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div
              className='rounded-lg p-3 text-xs text-foreground/70 leading-relaxed'
              style={{
                backgroundColor: '#8b5cf610',
                border: '1px solid #8b5cf620',
              }}
            >
              {result.aiSummary.context}
            </div>
          </div>
        </LenisFadeIn>
      )}

      {/* Bot Rules Table */}
      <LenisFadeIn>
        <BotRulesTable botAnalysis={result.botAnalysis} />
      </LenisFadeIn>

      {/* Score Breakdown */}
      <LenisStaggered>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
          {result.scoreBreakdown.map(cat => {
            const pct =
              cat.maxScore > 0
                ? Math.round((cat.score / cat.maxScore) * 100)
                : 0
            return (
              <div
                key={cat.category}
                className='bg-background border border-border/60 rounded-2xl p-5 flex flex-col gap-3'
              >
                <div className='flex items-center justify-between'>
                  <div className='flex items-center gap-2'>
                    <span
                      className='h-2 w-2 rounded-full'
                      style={{ backgroundColor: cat.color }}
                    />
                    <span className='text-sm font-semibold text-foreground'>
                      {cat.category}
                    </span>
                  </div>
                  <span
                    className='text-xs font-bold rounded-full px-2.5 py-0.5'
                    style={{
                      backgroundColor: `${cat.color}15`,
                      color: cat.color,
                    }}
                  >
                    {cat.score}/{cat.maxScore}
                  </span>
                </div>

                <div className='h-2 rounded-full bg-muted overflow-hidden'>
                  <div
                    className='h-full rounded-full transition-all duration-700 ease-out'
                    style={{ width: `${pct}%`, backgroundColor: cat.color }}
                  />
                </div>

                <p className='text-xs text-foreground/70 leading-relaxed'>
                  {cat.details}
                </p>
              </div>
            )
          })}
        </div>
      </LenisStaggered>

      {/* Directive Breakdown */}
      <LenisFadeIn>
        <DirectiveBreakdown
          blocks={result.userAgentBlocks}
          sitemapUrls={result.robotsTxt.sitemapUrls}
        />
      </LenisFadeIn>

      {/* Syntax Issues */}
      {result.syntaxIssues.length > 0 && (
        <LenisFadeIn>
          <div className='bg-muted/50 rounded-2xl border border-border/60 p-6'>
            <h2 className='text-lg font-semibold text-foreground mb-4'>
              Syntax Issues
            </h2>
            <div className='space-y-2'>
              {result.syntaxIssues.map((issue, i) => (
                <div
                  key={i}
                  className='flex items-start gap-3 bg-background border border-border/60 rounded-xl p-3.5'
                >
                  {issue.severity === 'error' ? (
                    <CircleX
                      size={14}
                      className='mt-0.5 shrink-0'
                      style={{ color: '#F90B8A' }}
                    />
                  ) : (
                    <AlertCircle
                      size={14}
                      className='mt-0.5 shrink-0'
                      style={{ color: '#FDC304' }}
                    />
                  )}
                  <div className='flex-1 min-w-0'>
                    <p className='text-sm text-foreground/80'>
                      {issue.message}
                    </p>
                    {issue.line > 0 && (
                      <span className='text-[10px] text-muted-foreground'>
                        Line {issue.line}
                      </span>
                    )}
                  </div>
                  <span
                    className='text-[10px] font-semibold uppercase rounded-full px-2 py-0.5 shrink-0'
                    style={{
                      backgroundColor:
                        issue.severity === 'error' ? '#F90B8A15' : '#FDC30415',
                      color: issue.severity === 'error' ? '#F90B8A' : '#FDC304',
                    }}
                  >
                    {issue.severity}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </LenisFadeIn>
      )}

      {/* Suggested Snippet */}
      {result.suggestedSnippet && (
        <LenisFadeIn>
          <div className='bg-muted/50 rounded-2xl border border-border/60 p-6'>
            <div className='flex items-center justify-between mb-4'>
              <h2 className='text-lg font-semibold text-foreground'>
                Suggested Additions
              </h2>
              <button
                onClick={copySnippet}
                className='flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors rounded-lg px-3 py-1.5 bg-background border border-border/60'
              >
                {snippetCopied ? (
                  <>
                    <Check size={14} style={{ color: '#22c55e' }} />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy size={14} />
                    Copy
                  </>
                )}
              </button>
            </div>
            <p className='text-sm text-muted-foreground mb-3'>
              Add these directives to your robots.txt for better AI bot
              coverage.
            </p>
            <pre className='text-xs text-foreground/80 bg-background border border-border/60 rounded-lg p-4 overflow-x-auto font-mono leading-relaxed whitespace-pre-wrap'>
              {result.suggestedSnippet}
            </pre>
          </div>
        </LenisFadeIn>
      )}

      {/* Recommendations */}
      {result.recommendations.length > 0 && (
        <LenisFadeIn>
          <div className='bg-muted/50 rounded-2xl border border-border/60 p-6'>
            <h2 className='text-lg font-semibold text-foreground mb-4'>
              Action Roadmap
            </h2>
            <div className='space-y-3'>
              {result.recommendations.map((rec, i) => {
                const pc = PRIORITY_COLORS[rec.priority]
                return (
                  <div
                    key={i}
                    className='flex items-start gap-3 bg-background border border-border/60 rounded-xl p-4'
                  >
                    <div className='mt-0.5'>{PRIORITY_ICONS[rec.priority]}</div>
                    <div className='flex-1 min-w-0'>
                      <div className='flex items-center gap-2 mb-1'>
                        <span
                          className='text-[10px] font-semibold uppercase rounded-full px-2 py-0.5'
                          style={{ backgroundColor: pc.bg, color: pc.text }}
                        >
                          {rec.priority}
                        </span>
                        <span className='text-[11px] text-muted-foreground'>
                          {rec.category}
                        </span>
                      </div>
                      <p className='text-sm text-foreground/80'>
                        {rec.message}
                      </p>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </LenisFadeIn>
      )}

      {/* Raw robots.txt */}
      {result.robotsTxt.rawContent && (
        <LenisFadeIn>
          <div className='bg-muted/50 rounded-2xl border border-border/60 p-6'>
            <button
              onClick={() => setRawExpanded(!rawExpanded)}
              className='flex items-center justify-between w-full'
            >
              <h2 className='text-lg font-semibold text-foreground'>
                Raw robots.txt
              </h2>
              <div className='flex items-center gap-2 text-muted-foreground'>
                <span className='text-xs'>
                  {result.robotsTxt.lineCount} lines,{' '}
                  {result.robotsTxt.fileSize > 1024
                    ? `${(result.robotsTxt.fileSize / 1024).toFixed(1)}KB`
                    : `${result.robotsTxt.fileSize}B`}
                </span>
                {rawExpanded ? (
                  <ChevronUp size={16} />
                ) : (
                  <ChevronDown size={16} />
                )}
              </div>
            </button>
            {rawExpanded && (
              <pre className='mt-4 text-xs text-foreground/70 bg-background border border-border/60 rounded-lg p-4 overflow-x-auto font-mono leading-relaxed whitespace-pre-wrap max-h-96 overflow-y-auto'>
                {result.robotsTxt.rawContent}
              </pre>
            )}
          </div>
        </LenisFadeIn>
      )}

      {/* CTA — public surface only */}
      {variant !== 'dashboard' && (
        <LenisFadeIn>
          <div
            className='relative overflow-hidden rounded-2xl p-8 md:p-10 text-center'
            style={{
              background: 'linear-gradient(135deg, #0f5e6b, #20808d, #2ba3b3)',
            }}
          >
            <h3 className='text-xl md:text-2xl font-bold text-white mb-3'>
              Want a complete AI visibility audit?
            </h3>
            <p
              className='text-sm mb-6 max-w-lg mx-auto'
              style={{ color: 'rgba(255,255,255,0.9)' }}
            >
              robots.txt is just one piece. Our full audit covers structured
              data, llms.txt, knowledge graphs, and citation tracking for your
              brand.
            </p>
            <div className='flex flex-wrap items-center justify-center gap-3'>
              <a
                href='/services/ai-visibility-strategy'
                className='inline-flex items-center justify-center h-11 px-6 rounded-full border-2 border-white text-white font-medium text-sm transition-all duration-300'
                style={{ backgroundColor: 'transparent' }}
                onMouseEnter={e => {
                  e.currentTarget.style.backgroundColor = 'white'
                  e.currentTarget.style.color = '#20808d'
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.backgroundColor = 'transparent'
                  e.currentTarget.style.color = 'white'
                }}
              >
                Get a Strategy Session
              </a>
              <Link
                href='/tools/ai-readiness-score'
                className='inline-flex items-center justify-center h-11 px-6 rounded-full text-sm font-medium transition-colors'
                style={{ color: 'rgba(255,255,255,0.9)' }}
              >
                Try AI Readiness Score <ArrowRight size={16} className='ml-1' />
              </Link>
            </div>
          </div>
        </LenisFadeIn>
      )}
    </div>
  )
}
